import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
// eslint-disable-next-line no-restricted-imports
import {
  Box,
  Container,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Tooltip,
  Collapse,
  SxProps,
  Theme,
} from '@mui/material';
import { useStarredEntities } from '@backstage/plugin-catalog-react';
import { SearchContextProvider } from '@backstage/plugin-search-react';
import { useApi, identityApiRef, discoveryApiRef, configApiRef } from '@backstage/core-plugin-api';
import { Link } from 'react-router-dom';
import {
  Send,
  ContentCopy,
  Launch,
  Stop,
  AddCircleOutline,
  ExpandMore,
} from '@mui/icons-material';
import { CardActionArea } from '@material-ui/core';
import { HomePageStarredEntities } from '@backstage/plugin-home';
import Markdown from 'markdown-to-jsx';
import { DynamicFormMessage, FormSubmitPayload } from '../chatbot/DynamicFormMessage';
import { DeploymentTracker, DeploymentStatusData } from '../chatbot/DeploymentTracker';

interface TemplateItem {
  template_name: string;
  template_title: string;
  template_description: string;
  template_type: string;
}

interface Message {
  id: number;
  type: 'user' | 'ai' | 'form';
  content: string;
  timestamp: string;
  isTyping?: boolean;
  formData?: any;
  formSubmitted?: boolean;
  templateList?: TemplateItem[];
  deploymentData?: DeploymentStatusData;
}

// ============================================================================
// THEME & STYLE CONSTANTS
// ============================================================================
const COLORS = {
  primary: '#F58321',
  primaryDark: '#E5751F',
  primaryDarker: '#D0671A',
  border: '#e0ddd8',
  bgLight: '#fafaf8',
  bgLighter: '#f5f5f0',
  white: '#fff',
  textPrimary: '#1a1a1a',
  textSecondary: '#444',
  textMuted: '#666',
  textLight: '#888',
  textPlaceholder: 'rgba(54, 54, 54, 0.5)',
  green: '#52C41A',
  gray: '#ececf0',
  cardBorder: '#E5E5E5',
} as const;

const STYLES = {
  // Card base styles
  cardBase: {
    bgcolor: COLORS.white,
    border: `1px solid ${COLORS.cardBorder}`,
    borderRadius: '16px !important',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
    transition: 'all 0.25s ease',
    '&:hover': {
      boxShadow: '0 2px 8px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.03)',
      transform: 'translateY(-2px)',
    },
  } as SxProps<Theme>,

  // Paper styles
  paperBase: {
    bgcolor: COLORS.white,
    border: `1px solid ${COLORS.border}`,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05) !important',
    borderRadius: '12px !important',
  } as SxProps<Theme>,

  // Stat card content
  statCardContent: {
    p: 2,
    '&:last-child': { pb: 2 },
  } as SxProps<Theme>,

  // Suggested question card
  suggestedAgentCard: {
    height: '100%',
    borderRadius: '14px',
    overflow: 'hidden',
    background: 'linear-gradient(160deg, #ffffff 0%, #fffbf7 100%)',
    border: '1px solid',
    borderColor: COLORS.border,
    position: 'relative',
    transition: 'all 0.25s ease',
    boxShadow: '0 2px 8px rgba(245, 131, 33, 0.06)',
    '&:hover': {
      boxShadow: '0 4px 16px rgba(245, 131, 33, 0.12)',
      borderColor: COLORS.primary,
      transform: 'translateY(-2px)',
    },
  } as SxProps<Theme>,

  // Suggested question item
  suggestionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    px: 1.25,
    py: 0.85,
    borderRadius: '8px',
    bgcolor: 'rgba(255,255,255,0.8)',
    cursor: 'pointer',
    border: '1px solid transparent',
    transition: 'all 0.2s ease',
    '&:hover': {
      bgcolor: COLORS.white,
      borderColor: COLORS.primary,
      boxShadow: '0 2px 8px rgba(245, 131, 33, 0.10)',
    },
  } as SxProps<Theme>,
} as const;

// Helper function for message bubble styles
const getMessageBubbleStyle = (isUser: boolean): SxProps<Theme> => ({
  p: '10px 14px',
  bgcolor: isUser ? `${COLORS.primary} !important` : `${COLORS.white} !important`,
  color: isUser ? `${COLORS.white} !important` : `${COLORS.textPrimary} !important`,
  maxWidth: 750,
  borderRadius: '12px !important',
  border: isUser ? 'none' : `1px solid ${COLORS.border}`,
  boxShadow: isUser ? 'none !important' : `0 2px 4px rgba(0, 0, 0, 0.05) !important`,
  wordBreak: 'break-word',
  '& code': {
    bgcolor: isUser ? 'rgba(255,255,255,0.2)' : `rgba(245, 131, 33, 0.1)`,
    px: 0.5,
    borderRadius: 0.5,
    fontFamily: 'monospace',
    fontSize: '0.9em',
  },
  '& pre': {
    bgcolor: isUser ? 'rgba(255,255,255,0.15)' : COLORS.bgLighter,
    p: 1,
    borderRadius: 1,
    overflowX: 'auto',
    margin: '0.5em 0',
  },
});

// Tool icons and links
const getTools = () => [
  {
    url: 'https://launcher.myapps.microsoft.com/api/signin/5ea53bdb-53f8-4931-be2e-1c3d37f0c658?tenantId=19220479-e2a8-4137-9aec-956ada3f2bd5',
    label: 'AWS',
    description: 'Amazon Web Services cloud infrastructure',
    icon: (
      <img
        src="https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg"
        alt="AWS"
        style={{ width: 40, height: 40 }}
      />
    ),
  },
  {
    url: 'https://portal.azure.com/signin/index/',
    label: 'Azure AD',
    description: 'Microsoft Azure cloud services',
    icon: (
      <img
        src="https://upload.wikimedia.org/wikipedia/commons/f/fa/Microsoft_Azure.svg"
        alt="Azure"
        style={{ width: 40, height: 40 }}
      />
    ),
  },
  {
    url: 'https://accounts.cloud.databricks.com/login?account_id=2f16886d-5680-4be5-8399-3aea0fae3870&next_url=%2Fworkspaces%3Faccount_id%3D2f16886d-5680-4be5-8399-3aea0fae3870&lakehouse=true',
    label: 'Databricks',
    description: 'Unified analytics platform for big data',
    icon: (
      <img
        src="/logos/databricks.svg"
        alt="DataBricks"
        style={{ width: 40, height: 40 }}
      />
    ),
  },
  {
    url: 'https://ascentt-systems-inc.ghe.com/Ascentt-Systems',
    label: 'GitHub',
    description: 'Source code repository and version control',
    icon: (
      <img
        src="/logos/github.png"
        alt="github"
        style={{ width: 40, height: 40 }}
      />
    ),
  },
];

const getSuggestedQuestions = () => [
  {
    category: 'Project Accelerator',
    primaryColor: COLORS.primary,
    bgColor: COLORS.white,
    borderColor: COLORS.border,
    questions: [
      'Create a project',
      'List all the available DevKits',
      'Raise a resource request',
    ],
  },
  {
    category: 'Service Discovery',
    primaryColor: COLORS.primary,
    bgColor: COLORS.white,
    borderColor: COLORS.border,
    questions: [
      'Applications deployed in prod',
      'Show existing project components',
      'Total cost of all the project components',
    ],
  },
  {
    category: 'Platform Concierge',
    primaryColor: COLORS.primary,
    bgColor: COLORS.white,
    borderColor: COLORS.border,
    questions: [
      'Show me the platform documentation',
      'Batch inference pipeline features',
      'Model training pipeline prerequisites',
    ],
  },
  {
    category: 'Deployment',
    primaryColor: COLORS.primary,
    bgColor: COLORS.white,
    borderColor: COLORS.border,
    questions: [
      'Deploy my application',
      'Check deployment status',
      'Show CI/CD workflows for my repo',
    ],
  },
];

const rotatingSubtitles = [
  'Ask me anything about the platform',
  'Get help with deployments and pipelines',
  'Explore services and APIs',
  'Learn about ML model management',
  'Discover platform tools and resources',
  'Find documentation and guides',
  'Get support for development workflows',
  'Access cost insights and metrics',
  'Manage your starred entities',
  'Navigate the developer portal',
];



interface StatsData {
  projects: {
    total: number;
    new: number;
  };
  models: {
    total: number;
    new: number;
  };
  docs: {
    total: number;
  };
  cost: {
    value: string;
    monthly: boolean;
  };
}

/**
 * Try to parse a chat message as a field value update for the active form.
 *
 * Recognized patterns:
 *   "field name is value"
 *   "field name: value"
 *   "set field name to value"
 *
 * Uses fuzzy matching against field titles and keys (case-insensitive,
 * ignores hyphens/underscores/spaces).
 *
 * Supports multi-field messages separated by "and", ".", or ";".
 * Example: "owner will be me and description is my project"
 */
function tryParseFieldUpdate(
  message: string,
  fields: Record<string, { title?: string }>,
): { updates: Record<string, string>; labels: Record<string, string> } | null {
  const text = message.trim();
  if (!text || !fields || Object.keys(fields).length === 0) return null;

  // Build a lookup: normalized form → { key, title }
  const normalize = (s: string) =>
    s.toLowerCase().replace(/[-_\s]+/g, '');

  const fieldLookup = Object.entries(fields).map(([key, schema]) => ({
    key,
    title: schema.title || key,
    norm: normalize(key),
    normTitle: normalize(schema.title || key),
  }));

  // Patterns: each captures (field, value). Last entry is reversed (value, field).
  const patterns: Array<{ re: RegExp; reversed?: boolean }> = [
    { re: /^set\s+(.+?)\s+to\s+(.+)$/i },
    { re: /^(.+?)\s+is\s+(.+)$/i },
    { re: /^(.+?):\s*(.+)$/ },
    { re: /^(.+?)\s+will\s+be\s+(.+)$/i },
    { re: /^(.+?)\s+should\s+be\s+(.+)$/i },
    { re: /^(.+?)\s*=\s*(.+)$/ },
    { re: /^use\s+(.+?)\s+(?:for|as)\s+(?:the\s+)?(.+)$/i, reversed: true },
  ];

  const findField = (rawField: string) => {
    const normField = normalize(rawField);
    // Exact match first
    const exact = fieldLookup.find(
      f => f.norm === normField || f.normTitle === normField,
    );
    if (exact) return exact;
    // Partial / substring match
    return (
      fieldLookup.find(
        f =>
          f.normTitle.includes(normField) ||
          f.norm.includes(normField) ||
          normField.includes(f.normTitle) ||
          normField.includes(f.norm),
      ) || null
    );
  };

  const tryMatch = (
    segment: string,
  ): { key: string; value: string; title: string } | null => {
    for (const { re, reversed } of patterns) {
      const match = segment.match(re);
      if (!match) continue;
      const rawField = reversed ? match[2].trim() : match[1].trim();
      const rawValue = reversed ? match[1].trim() : match[2].trim();
      const found = findField(rawField);
      if (found) return { key: found.key, value: rawValue, title: found.title };
    }
    return null;
  };

  // 1. Try the FULL message first (preserves values containing "and")
  const singleMatch = tryMatch(text);
  if (singleMatch) {
    return {
      updates: { [singleMatch.key]: singleMatch.value },
      labels: { [singleMatch.key]: singleMatch.title },
    };
  }

  // 2. Split on common delimiters for multi-field messages
  //    e.g. "owner will be me and description is my project"
  const segments = text
    .split(/\s+and\s+|\.\s+|;\s+/i)
    .map(s => s.trim())
    .filter(Boolean);
  if (segments.length <= 1) return null; // Already tried the full message

  const updates: Record<string, string> = {};
  const labels: Record<string, string> = {};

  for (const segment of segments) {
    const result = tryMatch(segment);
    if (result && !updates[result.key]) {
      updates[result.key] = result.value;
      labels[result.key] = result.title;
    }
  }

  return Object.keys(updates).length > 0 ? { updates, labels } : null;
}

// ============================================================================
// MEMOIZED MESSAGE COMPONENT
// ============================================================================
// Prevents re-rendering every historical message when a new token arrives.
// Only the message whose content/isTyping actually changed will re-render.
//
// Custom comparator: skip re-render when id, content, isTyping, templateList,
// deploymentData, and disabled are all unchanged.
interface ChatMessageProps {
  msg: Message;
  isUser: boolean;
  disabled: boolean;                                // true when bot is typing (disables template clicks)
  userProfile: { displayName?: string; email?: string; picture?: string } | null;
  onCopyContent: (text: string) => void;
  onSelectTemplate: (title: string) => void;
  onTriggerDeploy: (owner: string, repo: string, workflow: string) => void;
  onRefreshStatus: (owner: string, repo: string, runId?: string) => void;
}

const getUserInitialsStatic = (name?: string) => {
  if (!name) return 'U';
  return name
    .split(' ')
    .map(n => n.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('');
};

const ChatMessage = React.memo<ChatMessageProps>(
  ({ msg, isUser, disabled, userProfile, onCopyContent, onSelectTemplate, onTriggerDeploy, onRefreshStatus }) => (
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        mb: 2,
        justifyContent: isUser ? 'flex-end' : 'flex-start',
      }}
    >
      {!isUser && (
        <img src="/logos/bot.svg" width="32" height="32" />
      )}
      <Box>
        <Paper elevation={0} sx={getMessageBubbleStyle(isUser)}>
          <Typography variant="body2" component="div" sx={{ fontSize: '0.875rem', fontWeight: '500', lineHeight: 1.5 }}>
            <Markdown>{msg.content}</Markdown>
          </Typography>
        </Paper>
        {/* Clickable template cards */}
        {msg.templateList && msg.templateList.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5, maxWidth: 600 }}>
            {msg.templateList.map((tpl, idx) => (
              <Paper
                key={tpl.template_name}
                elevation={0}
                onClick={() => { if (!disabled) onSelectTemplate(tpl.template_title); }}
                sx={{
                  p: '12px 16px',
                  cursor: disabled ? 'default' : 'pointer',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '12px !important',
                  transition: 'all 0.2s ease',
                  opacity: disabled ? 0.6 : 1,
                  '&:hover': disabled ? {} : {
                    borderColor: COLORS.primary,
                    boxShadow: `0 0 0 1px ${COLORS.primary}`,
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: `${COLORS.primary}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: COLORS.primary }}>{idx + 1}</Typography>
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.84rem', color: COLORS.textPrimary, lineHeight: 1.3 }}>
                      {tpl.template_title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: COLORS.textMuted, fontSize: '0.75rem', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {tpl.template_description}
                    </Typography>
                  </Box>
                  <Launch sx={{ fontSize: 16, color: COLORS.textMuted, flexShrink: 0, ml: 'auto' }} />
                </Box>
              </Paper>
            ))}
          </Box>
        )}
        {/* Deployment tracker inline */}
        {msg.deploymentData && (
          <Box sx={{ mt: 1.5, maxWidth: 640 }}>
            <DeploymentTracker
              data={msg.deploymentData}
              onTriggerDeploy={onTriggerDeploy}
              onRefreshStatus={onRefreshStatus}
            />
          </Box>
        )}
        {!isUser && (
          <Box sx={{ display: 'flex', gap: 0.5, mt: 1, ml: 1 }}>
            <IconButton size="small" sx={{ p: 0.5 }} onClick={() => onCopyContent(msg.content)}>
              <ContentCopy sx={{ fontSize: 16, color: COLORS.textMuted }} />
            </IconButton>
          </Box>
        )}
      </Box>
      {isUser && (
        <Avatar
          src={userProfile?.picture}
          sx={{ width: 32, height: 32, bgcolor: COLORS.gray, color: '#030213', flexShrink: 0 }}
        >
          {!userProfile?.picture && getUserInitialsStatic(userProfile?.displayName)}
        </Avatar>
      )}
    </Box>
  ),
  (prev, next) =>
    prev.msg.id === next.msg.id &&
    prev.msg.content === next.msg.content &&
    prev.msg.isTyping === next.msg.isTyping &&
    prev.msg.templateList === next.msg.templateList &&
    prev.msg.deploymentData === next.msg.deploymentData &&
    prev.disabled === next.disabled,
);
ChatMessage.displayName = 'ChatMessage';

export const HomePage = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [inputValue, setInputValue] = useState('');
  const { starredEntities } = useStarredEntities();
  const [showConversation, setShowConversation] = useState(false);
  const [isChatBotTyping, setIsChatBotTyping] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [showTypingAnimation, setShowTypingAnimation] = useState(false);
  const [thinkingStatus, setThinkingStatus] = useState<string>('Thinking…');
  const [statusHistory, setStatusHistory] = useState<string[]>([]);
  const [currentSubtitleIndex, setCurrentSubtitleIndex] = useState(0);
  const [statsData, setStatsData] = useState<StatsData>({
    projects: { total: 0, new: 0 },
    models: { total: 0, new: 0 },
    docs: { total: 0 },
    cost: { value: '$0', monthly: true },
  });
  const [recentProjects, setRecentProjects] = useState<any[]>([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [activeForm, setActiveForm] = useState<{ id: number; formData: any } | null>(null);
  const [formFieldUpdates, setFormFieldUpdates] = useState<Record<string, string>>({});
  const [activeDeployment, setActiveDeployment] = useState<DeploymentStatusData | null>(null);
  const [reasoningExpanded, setReasoningExpanded] = useState(true);

  const identityApi = useApi(identityApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const configApi = useApi(configApiRef);
  const [userProfile, setUserProfile] = useState<{
    displayName?: string;
    email?: string;
    picture?: string;
  } | null>(null);

  const currentBotMessageId = useRef<number | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const chunkQueueRef = useRef<string[]>([]);
  const isProcessingChunksRef = useRef(false);
  const ignoreChunksRef = useRef(false);
  /** Accumulates token text between animation frames so we batch setMessages calls */
  const pendingChunkTextRef = useRef<string>('');
  const rafIdRef = useRef<number | null>(null);

  // ── Real-time deployment status polling ────────────────────────────
  // Directly polls the Backstage deployment backend every 10 s when a
  // deployment run is active (queued / in_progress).  Stops automatically
  // once the run completes.
  const pollOwner = activeDeployment?.type === 'deployment_status' ? activeDeployment.repo_owner : undefined;
  const pollRepo  = activeDeployment?.type === 'deployment_status' ? activeDeployment.repo_name  : undefined;
  const pollRunId = activeDeployment?.type === 'deployment_status' ? activeDeployment.run_id     : undefined;
  const isActiveRun =
    !!activeDeployment &&
    activeDeployment.type === 'deployment_status' &&
    (activeDeployment.status === 'in_progress' || activeDeployment.status === 'queued');

  useEffect(() => {
    if (!isActiveRun || !pollOwner || !pollRepo) return;

    let cancelled = false;

    const pollStatus = async () => {
      if (cancelled) return;
      try {
        const baseUrl = await discoveryApi.getBaseUrl('deployment');
        const { token } = await identityApi.getCredentials();
        const params = new URLSearchParams();
        if (pollRunId) params.set('runId', String(pollRunId));
        const qs = params.toString();
        const url = `${baseUrl}/status/${encodeURIComponent(pollOwner)}/${encodeURIComponent(pollRepo)}${qs ? `?${qs}` : ''}`;
        const resp = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!resp.ok || cancelled) return;
        const runData = await resp.json();
        if (cancelled) return;

        // Transform backend RunStatusResponse → DeploymentStatusData
        const jobs = (runData.jobs || []).map((job: any) => ({
          name: job.name || 'Unknown',
          status: job.status || 'queued',
          conclusion: job.conclusion ?? null,
          started_at: job.started_at ?? null,
          completed_at: job.completed_at ?? null,
          steps: (job.steps || []).map((step: any, idx: number) => ({
            name: step.name || 'Unknown',
            status: step.status || 'queued',
            conclusion: step.conclusion ?? null,
            number: step.number ?? idx + 1,
            started_at: step.started_at ?? null,
            completed_at: step.completed_at ?? null,
          })),
        }));

        setActiveDeployment(prev => prev ? {
          ...prev,
          status: runData.status || prev.status,
          conclusion: runData.conclusion ?? null,
          run_url: runData.html_url || prev.run_url,
          run_id: runData.run_id ?? prev.run_id,
          workflow_name: runData.name || prev.workflow_name,
          branch: runData.head_branch || prev.branch,
          commit_sha: runData.head_sha ? runData.head_sha.slice(0, 7) : prev.commit_sha,
          started_at: runData.created_at || prev.started_at,
          updated_at: runData.updated_at || prev.updated_at,
          jobs,
          error_messages: runData.error_messages || [],
        } : null);

        // Keep inline chat message in sync
        const rid = pollRunId ?? runData.run_id;
        if (rid) {
          setMessages(prev =>
            prev.map(m =>
              m.deploymentData?.run_id === rid
                ? {
                    ...m,
                    deploymentData: {
                      ...m.deploymentData!,
                      status: runData.status,
                      conclusion: runData.conclusion ?? null,
                      jobs,
                      error_messages: runData.error_messages || [],
                      updated_at: runData.updated_at,
                    },
                  }
                : m,
            ),
          );
        }
      } catch (e) {
        console.warn('[DevHub] Deployment status poll error:', e);
      }
    };

    // Poll immediately, then every 10 s
    pollStatus();
    const interval = setInterval(pollStatus, 10_000);
    return () => { cancelled = true; clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActiveRun, pollOwner, pollRepo, pollRunId, discoveryApi, identityApi]);

  // Memoize derived data
  const tools = useMemo(() => getTools(), []);
  const suggestedQuestions = useMemo(() => getSuggestedQuestions(), []);;

  const rotatingMessages = [
    `Hi ${userProfile?.displayName}, I'm ASC AI. I can help with deployments, pipelines, services, and related topics. How can I assist?`,
    `Welcome ${userProfile?.displayName}! I'm here to help you navigate the platform and answer your questions about development workflows.`,
    `Hello ${userProfile?.displayName}! Ask me about ML models, API documentation, or any platform-related queries you might have.`,
    `Greetings ${userProfile?.displayName}! I can assist with cost insights, service discovery, and best practices for your projects.`,
    `Hi there ${userProfile?.displayName}! Need help with infrastructure, deployment strategies, or finding the right tools? I'm here to help.`,
    `Welcome to DevHub ${userProfile?.displayName}! I can guide you through platform features, troubleshooting, and resource management.`,
  ];

  const [currentMessageIndex] = useState(
    Math.floor(Math.random() * rotatingMessages.length),
  );

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSubtitleIndex(prev => (prev + 1) % rotatingSubtitles.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    identityApi
      .getProfileInfo()
      .then(profile => {
        setUserProfile(profile);
        setUserId(profile?.email || 'user');
      })
      .catch(() => {
        setUserProfile({ displayName: 'User' });
        setUserId('user');
      });
  }, [identityApi]);

  useEffect(() => {
    const fetchStatsData = async () => {
      setStatsLoading(true);
      const userData = await identityApi.getProfileInfo();
      try {
        const baseUrl = await discoveryApi.getBaseUrl('catalog');
        const { token } = await identityApi.getCredentials();

        const headers: HeadersInit = {
          'Content-Type': 'application/json',
        };
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        // Repeated filter params are OR'd, so this counts both ordinary projects
        // and proof-of-concept projects, which are hidden from the catalog list
        // but should still show in the total.
        const projectsResponse = await fetch(
          `${baseUrl}/entities?filter=kind%3Dcomponent%2Cspec.type%3Dproject&filter=kind%3Dcomponent%2Cspec.type%3Dpoc`,
          { headers }
        );
        const projectsData = await projectsResponse.json();
        const totalProjects = projectsData?.length || 0;

        const componentResponse = await fetch(
          `${baseUrl}/entities?filter=kind%3Dcomponent`,
          { headers }
        );
        const componentData = await componentResponse.json();
        if (componentData && Array.isArray(componentData)) {
          componentData.reverse();
        }
        

        const userEmail = `user:default/${userData?.email?.replace('@', '_')}`;
        const userProjects = componentData?.filter((entity: any) => {
          const requestedBy = entity.spec?.['requestedBy'];
          return requestedBy === userEmail;
        }) || [];
        setRecentProjects(userProjects.slice(0, 3));

        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const newProjects = projectsData?.filter((entity: any) => {
          const createdDate = entity.metadata?.annotations?.['backstage.io/created-at'];
          return createdDate && new Date(createdDate) > thirtyDaysAgo;
        }).length || 0;

        const modelsResponse = await fetch(
          `${baseUrl}/entities?filter=spec.type=model`,
          { headers }
        );
        const modelsData = await modelsResponse.json();
        const totalModels = modelsData?.length || 0;

        const newModels = modelsData?.filter((entity: any) => {
          const createdDate = entity.metadata?.annotations?.['backstage.io/created-at'];
          return createdDate && new Date(createdDate) > thirtyDaysAgo;
        }).length || 0;

        const docsResponse = await fetch(
          `${baseUrl}/entities?filter=kind%3Dcomponent%2Cspec.type%3Ddocumentation`,
          { headers }
        );
        const docsData = await docsResponse.json();
        const totalDocs = docsData?.length || 0;

        let totalCost = 0;
        try {
          const today = new Date().toISOString().split('T')[0];
          const thirtyDaysAgoDate = new Date();
          thirtyDaysAgoDate.setDate(thirtyDaysAgoDate.getDate() - 30);
          const startDate = thirtyDaysAgoDate.toISOString().split('T')[0];

          const costUrl = `${baseUrl.replace('/catalog', '')}/aws-cost-explorer/cost`;
          const costBody = {
            dateRange: { startDate, endDate: today },
            granularity: 'DAILY',
            metric: 'UnblendedCost',
            filters: [],
          };

          const costResponse = await fetch(costUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify(costBody),
          });
          if (costResponse.ok) {
            const costData = await costResponse.json();
            const timeSeries = costData?.timeSeries || [];
            totalCost = timeSeries.reduce((sum: number, item: any) => sum + (parseFloat(item.amount) || 0), 0);
          }
        } catch (err) {
          // Silently handle error - cost will remain 0
        }

        setStatsData({
          projects: {
            total: totalProjects,
            new: newProjects,
          },
          models: {
            total: totalModels,
            new: newModels,
          },
          docs: {
            total: totalDocs,
          },
          cost: {
            value: totalCost > 0 ? `$${totalCost.toFixed(0)}` : '$0',
            monthly: true,
          },
        });
      } catch (error) {
        console.error('Error fetching stats data:', error);
      } finally {
        setStatsLoading(false);
      }
    };

    fetchStatsData();
  }, [identityApi, discoveryApi]);

  // ── Batched chunk rendering (replaces per-chunk setTimeout queue) ──
  //
  // Instead of queuing each token and processing them one-by-one with a 40ms
  // delay (which introduces artificial latency proportional to token count),
  // we accumulate incoming text in a ref and flush it to React state once per
  // animation frame (~16ms).  This dramatically reduces the number of
  // setMessages() calls and React re-renders while still producing smooth
  // streaming text.

  /** Flush accumulated token text to messages state (called via rAF) */
  const flushPendingChunks = useCallback(() => {
    rafIdRef.current = null;
    const text = pendingChunkTextRef.current;
    if (!text) return;
    pendingChunkTextRef.current = '';

    // Hide typing animation when first chunk arrives
    setShowTypingAnimation(false);

    setMessages(prev => {
      const lastMsg = prev[prev.length - 1];
      if (prev.length > 0 && lastMsg.type === 'ai' && lastMsg.isTyping) {
        const updated = [...prev];
        updated[updated.length - 1] = {
          ...updated[updated.length - 1],
          content: updated[updated.length - 1].content + text,
          isTyping: true,
        };
        return updated;
      }

      const newId = Date.now();
      currentBotMessageId.current = newId;
      return [
        ...prev,
        {
          id: newId,
          content: text,
          type: 'ai' as const,
          isTyping: true,
          timestamp: new Date().toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
          }),
        },
      ];
    });
    setIsChatBotTyping(true);
  }, []);

  /** Handle end-of-stream marker */
  const finalizeStream = useCallback(() => {
    // Flush any remaining text first — but only if we're not ignoring
    // chunks (template/form handler already discarded the buffer).
    if (pendingChunkTextRef.current && !ignoreChunksRef.current) {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      flushPendingChunks();
    } else {
      // Discard leftover buffer & cancel pending rAF
      pendingChunkTextRef.current = '';
      if (rafIdRef.current) { cancelAnimationFrame(rafIdRef.current); rafIdRef.current = null; }
    }

    ignoreChunksRef.current = false;
    setMessages(prev => {
      if (
        prev.length > 0 &&
        prev[prev.length - 1].id === currentBotMessageId.current
      ) {
        const updated = [...prev];
        updated[updated.length - 1] = {
          ...updated[updated.length - 1],
          isTyping: false,
        };
        currentBotMessageId.current = null;
        return updated;
      }
      currentBotMessageId.current = null;
      return prev;
    });
    setIsChatBotTyping(false);
    setShowTypingAnimation(false);
  }, [flushPendingChunks]);

  const chunkHandler = useCallback((chunk: string) => {
    if (chunk === 'EOS') {
      finalizeStream();
      return;
    }
    // Skip non-EOS chunks after template/form events
    if (ignoreChunksRef.current) return;

    // Accumulate text and schedule a rAF flush (batches multiple tokens
    // arriving in the same frame into a single React update)
    pendingChunkTextRef.current += chunk;
    if (!rafIdRef.current) {
      rafIdRef.current = requestAnimationFrame(flushPendingChunks);
    }
  }, [finalizeStream, flushPendingChunks]);

  const formHandler = useCallback((formData: any) => {
    console.log('[DevHub] formHandler called with:', formData?.template_title, 'fields:', Object.keys(formData?.fields || {}));
    ignoreChunksRef.current = true;  // Ignore subsequent text chunks
    // Discard any buffered tokens so they don't flush into a duplicate message
    pendingChunkTextRef.current = '';
    if (rafIdRef.current) { cancelAnimationFrame(rafIdRef.current); rafIdRef.current = null; }
    // Stop any typing state and typing animation
    setShowTypingAnimation(false);
    setIsChatBotTyping(false);

    const formId = Date.now();
    const noticeText = `Please fill in the **${formData.template_title || 'project'}** configuration form on the right →`;

    // Replace the current streaming AI message (verbose LLM text) with a short notice.
    // This avoids showing both the long "You've selected..." text AND the notice.
    setMessages(prev => {
      const updated = [...prev];
      if (currentBotMessageId.current) {
        const idx = updated.findIndex(m => m.id === currentBotMessageId.current);
        if (idx >= 0) {
          updated[idx] = {
            ...updated[idx],
            content: noticeText,
            isTyping: false,
          };
          currentBotMessageId.current = null;
          return updated;
        }
      }
      // Fallback: no active streaming message — append as new
      currentBotMessageId.current = null;
      return [
        ...updated,
        {
          id: formId,
          type: 'ai' as const,
          content: noticeText,
          timestamp: new Date().toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
          }),
        },
      ];
    });

    // Show form in the right panel, replacing widgets
    setActiveForm({ id: formId, formData });
  }, []);

  const templateListHandler = useCallback((templates: TemplateItem[]) => {
    console.log('[DevHub] templateListHandler called with', templates.length, 'templates');
    ignoreChunksRef.current = true;  // Ignore subsequent text chunks
    // Discard any buffered tokens so they don't flush into a duplicate message
    pendingChunkTextRef.current = '';
    if (rafIdRef.current) { cancelAnimationFrame(rafIdRef.current); rafIdRef.current = null; }
    setShowTypingAnimation(false);
    setIsChatBotTyping(false);

    const shortNotice = 'Here are the available kits — pick one to get started:';

    // Replace the current streaming AI message (verbose LLM text with button
    // tags and descriptions) with a short notice + clickable template cards.
    setMessages(prev => {
      const updated = [...prev];

      // Try to find the active streaming message first
      let targetIdx = -1;
      if (currentBotMessageId.current) {
        targetIdx = updated.findIndex(m => m.id === currentBotMessageId.current);
      }
      // Fallback: find the last AI message (EOS may have already cleared the ref)
      if (targetIdx < 0) {
        for (let i = updated.length - 1; i >= 0; i--) {
          if (updated[i].type === 'ai') {
            targetIdx = i;
            break;
          }
        }
      }

      if (targetIdx >= 0) {
        updated[targetIdx] = {
          ...updated[targetIdx],
          content: shortNotice,
          templateList: templates,
          isTyping: false,
        };
        currentBotMessageId.current = null;
        return updated;
      }

      // Last resort: create a new message with template cards
      currentBotMessageId.current = null;
      return [
        ...updated,
        {
          id: Date.now(),
          type: 'ai' as const,
          content: shortNotice,
          timestamp: new Date().toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
          }),
          templateList: templates,
        },
      ];
    });
  }, []);

  const handleFormSubmit = useCallback(async (payload: FormSubmitPayload) => {
    // Clear the form and any pending field updates from the right panel
    setActiveForm(null);
    setFormFieldUpdates({});

    // Send the form response as a JSON string through the normal message flow.
    // The backend detects the JSON form payload (with data.confirmed) in the
    // supervisor and routes it directly to the sub-agent tool, bypassing the LLM.
    // This works for both submit (confirmed=true) and cancel (confirmed=false).
    const formPayload = JSON.stringify(payload);
    const displayText = payload.data.confirmed
      ? `Submitted project configuration (${Object.values(payload.data.parameter_values).filter(v => v).length} parameters).`
      : 'Cancelled project creation.';
    processMessage(formPayload, displayText);
  }, [userId, sessionId, identityApi]);

  const processMessage = async (text: string, displayText?: string, suppressTokens?: boolean) => {
    if (!text.trim() || !userId) return;
    // When selecting a template, suppress leaked sub-agent LLM tokens so
    // only the thinking indicator is shown until the form event arrives.
    ignoreChunksRef.current = suppressTokens ?? false;

    const userMessage: Message = {
      id: Date.now(),
      type: 'user',
      content: displayText || text,
      timestamp: new Date().toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      }),
    };

    setMessages(prev => [...prev, userMessage]);
    setShowConversation(true);
    setIsChatBotTyping(true);
    setShowTypingAnimation(true);
    setReasoningExpanded(true);
    setThinkingStatus('Thinking…');
    setStatusHistory([]);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const agenticBaseUrl = configApi.getString('app.agenticUrl');

    // Retry configuration for transient network errors
    const MAX_RETRIES = 2;
    const BASE_DELAY_MS = 1000;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        // Per-request timeout — abort if the server takes too long to respond.
        // This is separate from the user-controlled AbortController.
        const timeoutId = setTimeout(() => {
          if (!controller.signal.aborted) controller.abort();
        }, 60_000); // 60 s server timeout

        const response = await fetch(`${agenticBaseUrl}/api/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${await identityApi.getCredentials().then(c => c.token)}`,
          },
          body: JSON.stringify({
            user_id: userId,
            message: text,
            session_id: sessionId,
          }),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        // ── Handle HTTP errors ──────────────────────────────────────
        if (!response.ok) {
          // Don't retry on client errors (4xx) — only on server errors
          if (response.status >= 400 && response.status < 500) {
            throw new Error(`Request failed (${response.status})`);
          }
          // 5xx — retry if we have attempts left
          if (attempt < MAX_RETRIES) {
            const delay = BASE_DELAY_MS * Math.pow(2, attempt);
            setThinkingStatus('Reconnecting…');
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }
          throw new Error(`Server error (${response.status})`);
        }

        if (!response.body) throw new Error('No response body');

        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        let buffer = '';
        let done = false;
        let currentEventType = 'token';

        while (!done) {
          const { done: streamDone, value } = await reader.read();
          done = streamDone;
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Parse SSE events
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line
          for (const line of lines) {
            if (line.startsWith('event: ')) {
              currentEventType = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
              const data = line.slice(6);
              try {
                const parsed = JSON.parse(data);

                if (currentEventType === 'status' && typeof parsed === 'string') {
                  // Handle status event — update thinking indicator text
                  setStatusHistory(prev => {
                    // Avoid duplicate consecutive statuses
                    if (prev.length > 0 && prev[prev.length - 1] === parsed) return prev;
                    return [...prev, parsed];
                  });
                  setThinkingStatus(parsed);
                } else if (currentEventType === 'form' && typeof parsed === 'object') {
                  // Handle form event — render dynamic form in right panel
                  console.log('[DevHub] Received form event:', parsed);
                  formHandler(parsed);
                } else if (currentEventType === 'template_list' && Array.isArray(parsed)) {
                  // Handle template_list event — render clickable template cards
                  console.log('[DevHub] Received template_list event:', parsed.length, 'templates');
                  templateListHandler(parsed);
                } else if (currentEventType === 'field_update' && typeof parsed === 'object') {
                  // Handle field_update event — LLM extracted field values
                  // from a natural language message while form is open.
                  console.log('[DevHub] Received field_update event:', parsed);
                  setFormFieldUpdates(prev => ({ ...prev, ...parsed }));
                } else if (currentEventType === 'deployment_status' && typeof parsed === 'object') {
                  // Handle deployment_status event — deployment tracker data
                  console.log('[DevHub] Received deployment_status event:', parsed);
                  const deploymentData = parsed as DeploymentStatusData;
                  // Attach to the current bot message for inline rendering
                  setMessages(prev =>
                    prev.map(m =>
                      m.id === currentBotMessageId.current
                        ? { ...m, deploymentData }
                        : m,
                    ),
                  );
                  // Also set as active deployment to show in right sidebar
                  setActiveDeployment(deploymentData);
                } else if (typeof parsed === 'string') {
                  chunkHandler(parsed);
                } else if (parsed.session_id) {
                  setSessionId(parsed.session_id);
                } else if (parsed.error) {
                  // Optional: Display error in chat
                }
              } catch (e) {
                // Not JSON, treat as raw text or ignore
              }
              // Reset event type after processing data
              currentEventType = 'token';
            }
          }
        }
        chunkHandler('EOS');
        // Success — break out of retry loop
        break;
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          // User stopped the stream — finalize current bot message
          chunkHandler('EOS');
          return;
        }

        // Retry on network errors (TypeError from fetch = network failure)
        if (error instanceof TypeError && attempt < MAX_RETRIES) {
          const delay = BASE_DELAY_MS * Math.pow(2, attempt);
          console.warn(`[DevHub] SSE network error, retrying in ${delay}ms (attempt ${attempt + 1}/${MAX_RETRIES})`, error);
          setThinkingStatus('Reconnecting…');
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }

        console.error('Error sending message:', error);
        const errorMessage: Message = {
          id: Date.now() + 1,
          type: 'ai',
          content:
            "I'm currently unable to connect to the AI service. Please check your connection and try again.",
          timestamp: new Date().toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
          }),
        };
        setMessages(prev => [...prev, errorMessage]);
        setIsChatBotTyping(false);
        setShowTypingAnimation(false);
      }
    }
  };

  const handleQuestionClick = (question: string) => {
    processMessage(question);
  };

  // ── Stable callbacks for memoized ChatMessage ─────────────────────
  const handleCopyContent = useCallback((text: string) => {
    navigator.clipboard.writeText(text);
  }, []);

  const handleSelectTemplate = useCallback((title: string) => {
    processMessage(`I want to use "${title}"`, undefined, true);
  }, [userId, sessionId, identityApi]);

  const handleMsgTriggerDeploy = useCallback((owner: string, repo: string, workflow: string) => {
    processMessage(`Deploy repository ${owner}/${repo} using workflow ${workflow}`);
  }, [userId, sessionId, identityApi]);

  const handleMsgRefreshStatus = useCallback((owner: string, repo: string, runId?: string) => {
    processMessage(
      runId
        ? `Check deployment status for ${owner}/${repo} run ${runId}`
        : `Check deployment status for ${owner}/${repo}`,
    );
  }, [userId, sessionId, identityApi]);

  const handleSendMessage = () => {
    if (!inputValue.trim()) return;

    // When the form is active, try to match the message to a form field.
    // e.g. "catalog name is xyz" → update the catalogName field in the form.
    if (activeForm) {
      const matched = tryParseFieldUpdate(inputValue, activeForm.formData?.fields || {});
      if (matched) {
        setFormFieldUpdates(prev => ({ ...prev, ...matched.updates }));
        // Show a confirmation in chat instead of sending to backend
        const fieldNames = Object.values(matched.labels).join(', ');
        setMessages(prev => [
          ...prev,
          {
            id: Date.now(),
            type: 'user' as const,
            content: inputValue,
            timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          },
          {
            id: Date.now() + 1,
            type: 'ai' as const,
            content: `✅ Updated **${fieldNames}** in the form. You can continue filling in the remaining fields or submit when ready.`,
            timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          },
        ]);
        setInputValue('');
        return;
      }
    }

    processMessage(inputValue);
    setInputValue('');
  };

  const handleStopStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    // Clear pending chunks & rAF
    chunkQueueRef.current = [];
    isProcessingChunksRef.current = false;
    pendingChunkTextRef.current = '';
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    setIsChatBotTyping(false);
    setShowTypingAnimation(false);
  }, []);

  const handleNewSession = useCallback(async () => {
    handleStopStreaming();

    // Delete session on backend
    if (sessionId) {
      try {
        const agenticBaseUrl = configApi.getString('app.agenticUrl');
        await fetch(
          `${agenticBaseUrl}/api/chat/sessions/${encodeURIComponent(sessionId)}`,
          { method: 'DELETE' },
        );
      } catch (e) {
        console.warn('Could not delete session:', e);
      }
    }

    // Reset all chat state
    setSessionId(null);
    setMessages([]);
    setShowConversation(false);
    setActiveForm(null);
    setFormFieldUpdates({});
    setActiveDeployment(null);
    setInputValue('');
    currentBotMessageId.current = null;
  }, [sessionId, handleStopStreaming]);

  // console.log('starredEntities:', starredEntities);
  return (
    <SearchContextProvider>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        {/* Main Content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {/* Content Area */}
          <Container maxWidth={false} sx={{ mt: 2, mb: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', px: { xs: 2, md: 3 } }}>
            {/* Welcome Header */}
            <Box id="tour-home-header" sx={{ mb: 2, flexShrink: 0, position: 'sticky', top: 0, zIndex: 11, background: 'linear-gradient(118deg, #EEEEEE 5.1%, #FFF1E7 93%)', marginTop: '-16px', padding: '10px 0' }}>
              <Typography
                variant="body3"
                sx={{ fontSize: '1.2rem', fontWeight: '600', display: 'block' }}
              >
                Welcome to DevHub
              </Typography>
              <Typography
                variant="body3"
                color="text.secondary"
                sx={{ fontWeight: '600', display: 'block' }}
              >
                Your central platform for managing applications, pipelines, and
                services.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
              {/* Left: AI Chat Section */}
              <Box id="tour-ai-chat" sx={{ display: 'flex', flexDirection: 'column', flex: activeForm ? '0 0 calc(41.666% - 8px)' : activeDeployment ? '0 0 calc(58.333% - 8px)' : '0 0 calc(66.666% - 8px)', minHeight: 0, transition: 'all 0.3s ease', position: 'sticky', top: 83, height: 'calc(100vh - 235px)' }}>
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '16px !important',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                  }}
                >
                  {/* Chat Header */}
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      borderBottom: `1px solid ${COLORS.border}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      background: 'linear-gradient(135deg, #fafaf8 0%, #f5f5f0 100%)',
                      flexShrink: 0,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <img src="/logos/bot.svg" width="24" height="24" />
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          color: COLORS.textPrimary,
                        }}
                      >
                        ASC AI
                      </Typography>
                    </Box>
                    {showConversation && (
                      <Tooltip title="Start new conversation" arrow>
                        <IconButton
                          size="small"
                          onClick={handleNewSession}
                          sx={{
                            color: COLORS.primary,
                            border: `1px solid ${COLORS.border}`,
                            borderRadius: '8px',
                            px: 1.5,
                            py: 0.5,
                            gap: 0.5,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: COLORS.primary,
                              bgcolor: 'rgba(245, 131, 33, 0.08)',
                            },
                          }}
                        >
                          <AddCircleOutline sx={{ fontSize: 16 }} />
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              color: COLORS.primary,
                            }}
                          >
                            New Chat
                          </Typography>
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  {/* Chat Messages Area */}
                  <Box
                    sx={{
                      flex: 1,
                      px: 2,
                      py: 1.5,
                      overflowY: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                      bgcolor: COLORS.bgLight,
                    }}
                  >
                    {!showConversation ? (
                      <>
                        {/* Initial AI Message */}
                        <Box sx={{ display: 'flex', gap: 1.5, mt: 2, mb: 1.5 }}>
                          <img src="/logos/bot.svg" width="32" height="32" style={{ flexShrink: 0 }} />
                          <Box>
                            <Paper
                              elevation={0}
                              sx={{
                                ...STYLES.paperBase,
                                p: '10px 14px',
                                maxWidth: 750,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: '0.875rem',
                                  fontWeight: '500',
                                  color: COLORS.textPrimary,
                                  lineHeight: 1.5,
                                }}
                              >
                                {rotatingMessages[currentMessageIndex]}
                              </Typography>
                            </Paper>
                          </Box>
                        </Box>

                        {/* Suggested Questions */}
                        <Box sx={{ mt: 0 }}>
                          <Grid container spacing={2}>
                            {suggestedQuestions.map((agent, idx) => (
                              <Grid item xs={12} sm={6} md={3} key={idx}>
                                <Paper
                                  elevation={0}
                                  sx={STYLES.suggestedAgentCard}
                                >
                                  {/* Agent Header */}
                                  <Box
                                    sx={{
                                      px: 1.25,
                                      py: 1,
                                      position: 'relative',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    {/* Agent Info */}
                                    <Typography
                                      variant="subtitle2"
                                      sx={{
                                        fontWeight: '700',
                                        fontSize: '0.85rem',
                                        textAlign: 'center',
                                        color: COLORS.textPrimary,
                                        letterSpacing: '-0.01em',
                                      }}
                                    >
                                      {agent.category} Agent
                                    </Typography>
                                  </Box>

                                  {/* Divider */}
                                  <Box
                                    sx={{
                                      height: '1px',
                                      background: 'linear-gradient(90deg, transparent, #f0ebe4, transparent)',
                                      mx: 1,
                                    }}
                                  />

                                  {/* Questions Section */}
                                  <Box sx={{ px: 1, pb: 1, pt: 0.5 }}>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.5,
                                      }}
                                    >
                                      {agent.questions.map((question, qIdx) => (
                                        <Box
                                          key={qIdx}
                                          onClick={() => handleQuestionClick(question)}
                                          sx={STYLES.suggestionItem}
                                        >
                                          <Send sx={{ fontSize: 12, color: COLORS.primary, opacity: 0.5, flexShrink: 0, transform: 'rotate(-45deg)' }} />
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontSize: '0.8rem',
                                              lineHeight: 1.4,
                                              flex: 1,
                                              fontWeight: 500,
                                              color: COLORS.textSecondary,
                                            }}
                                          >
                                            {question}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                  </Box>
                                </Paper>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      </>
                    ) : (
                      <>
                        {/* Initial AI Message */}
                        <Box sx={{ display: 'flex', gap: 1.5, mb: 0 }}>
                          <img src="/logos/bot.svg" width="32" height="32" style={{ flexShrink: 0 }} />
                          <Box>
                            <Paper
                              elevation={0}
                              sx={{
                                ...STYLES.paperBase,
                                p: '10px 14px',
                                maxWidth: 750,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: '0.875rem',
                                  fontWeight: '500',
                                  color: COLORS.textPrimary,
                                  lineHeight: 1.5,
                                }}
                              >
                                {rotatingMessages[currentMessageIndex]}
                              </Typography>
                            </Paper>
                          </Box>
                        </Box>

                        {/* Conversation Messages */}
                        {messages.map(msg => (
                          <ChatMessage
                            key={msg.id}
                            msg={msg}
                            isUser={msg.type === 'user'}
                            disabled={isChatBotTyping}
                            userProfile={userProfile}
                            onCopyContent={handleCopyContent}
                            onSelectTemplate={handleSelectTemplate}
                            onTriggerDeploy={handleMsgTriggerDeploy}
                            onRefreshStatus={handleMsgRefreshStatus}
                          />
                        ))}

                        {/* Copilot-style reasoning disclosure */}
                        {showTypingAnimation && (
                          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, alignItems: 'flex-start' }}>
                            <img
                              src="/logos/bot.svg"
                              width="32"
                              height="32"
                              style={{ flexShrink: 0 }}
                              alt=""
                            />
                            <Box sx={{ maxWidth: 380 }}>
                              {/* Reasoning toggle header */}
                              <Box
                                onClick={() => setReasoningExpanded(prev => !prev)}
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  cursor: 'pointer',
                                  userSelect: 'none',
                                  py: 0.25,
                                  px: 0.5,
                                  borderRadius: '6px',
                                  transition: 'background 0.15s',
                                  '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                                }}
                              >
                                <CircularProgress
                                  size={12}
                                  thickness={5}
                                  sx={{ color: COLORS.textMuted, flexShrink: 0 }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    color: COLORS.textMuted,
                                    letterSpacing: '0.01em',
                                  }}
                                >
                                  {thinkingStatus}
                                </Typography>
                                <ExpandMore
                                  sx={{
                                    fontSize: 16,
                                    color: COLORS.textLight,
                                    transition: 'transform 0.2s',
                                    transform: reasoningExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                  }}
                                />
                              </Box>

                              {/* Collapsible reasoning steps */}
                              <Collapse in={reasoningExpanded} timeout={200}>
                                <Box
                                  sx={{
                                    mt: 0.5,
                                    ml: 0.5,
                                    pl: 1.5,
                                    borderLeft: '2px solid rgba(0,0,0,0.08)',
                                    maxHeight: 180,
                                    overflowY: 'auto',
                                    '&::-webkit-scrollbar': { width: '3px' },
                                    '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.1)', borderRadius: '3px' },
                                  }}
                                >
                                  {statusHistory.slice(0, -1).map((step, idx) => (
                                    <Typography
                                      key={idx}
                                      variant="caption"
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        fontSize: '0.72rem',
                                        color: COLORS.textLight,
                                        lineHeight: 1.6,
                                        '&::before': {
                                          content: '"✓"',
                                          fontSize: '0.6rem',
                                          color: COLORS.green,
                                          fontWeight: 700,
                                        },
                                      }}
                                    >
                                      {step}
                                    </Typography>
                                  ))}
                                  {/* Current active step */}
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.75,
                                      fontSize: '0.72rem',
                                      color: COLORS.textSecondary,
                                      fontWeight: 500,
                                      lineHeight: 1.6,
                                      '@keyframes fadeIn': {
                                        '0%': { opacity: 0 },
                                        '100%': { opacity: 1 },
                                      },
                                      animation: 'fadeIn 0.25s ease-out',
                                    }}
                                  >
                                    <CircularProgress size={9} thickness={5} sx={{ color: COLORS.primary, flexShrink: 0 }} />
                                    {thinkingStatus}
                                  </Typography>
                                </Box>
                              </Collapse>
                            </Box>
                          </Box>
                        )}

                        <div ref={messagesEndRef} />
                      </>
                    )}
                  </Box>

                  {/* Chat Input */}
                  <Box
                    id="tour-chat-input"
                    sx={{
                      px: 2,
                      py: '14px',
                      borderTop: `2px solid ${COLORS.bgLighter}`,
                      background: 'linear-gradient(135deg, #fafaf8 0%, #f5f5f0 100%)',
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Ask about deployments, pipelines, services..."
                        value={inputValue}
                        onChange={e => setInputValue(e.target.value)}
                        onKeyPress={e => {
                          if (e.key === 'Enter') {
                            handleSendMessage();
                          }
                        }}
                        sx={{
                          fontSize: '0.875rem',
                          '& .MuiOutlinedInput-root': {
                            bgcolor: COLORS.white,
                            fontWeight: 500,
                            borderRadius: '10px !important',
                            borderColor: COLORS.border,
                            '& fieldset': {
                              borderRadius: '10px !important',
                              borderColor: COLORS.border,
                            },
                            '&:hover fieldset': {
                              borderColor: COLORS.primary,
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: COLORS.primary,
                            },
                          },
                          '& .MuiInputBase-input::placeholder': {
                            color: `${COLORS.textPlaceholder} !important`,
                            opacity: 1,
                          },
                        }}
                      />
                      {isChatBotTyping ? (
                        <Tooltip title="Stop generating" arrow>
                          <IconButton
                            onClick={handleStopStreaming}
                            sx={{
                              background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`,
                              color: COLORS.white,
                              borderRadius: '10px',
                              width: 42,
                              height: 42,
                              boxShadow: '0 2px 8px rgba(245, 131, 33, 0.3)',
                              transition: 'all 0.2s ease',
                              animation: 'pulse 2s infinite',
                              '@keyframes pulse': {
                                '0%, 100%': { boxShadow: '0 2px 8px rgba(245, 131, 33, 0.3)' },
                                '50%': { boxShadow: '0 4px 16px rgba(245, 131, 33, 0.5)' },
                              },
                              '&:hover': {
                                background: `linear-gradient(135deg, ${COLORS.primaryDark} 0%, ${COLORS.primaryDarker} 100%)`,
                                boxShadow: '0 4px 12px rgba(245, 131, 33, 0.4)',
                                transform: 'scale(1.05)',
                              },
                            }}
                          >
                            <Stop fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <IconButton
                          onClick={handleSendMessage}
                          disabled={!inputValue.trim()}
                          sx={{
                            background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`,
                            color: COLORS.white,
                            borderRadius: '10px',
                            width: 42,
                            height: 42,
                            boxShadow: '0 2px 8px rgba(245, 131, 33, 0.3)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              background: `linear-gradient(135deg, ${COLORS.primaryDark} 0%, ${COLORS.primaryDarker} 100%)`,
                              boxShadow: '0 4px 12px rgba(245, 131, 33, 0.4)',
                              transform: 'scale(1.05)',
                            },
                            '&.Mui-disabled': {
                              background: COLORS.bgLighter,
                              color: COLORS.textLight,
                              boxShadow: 'none',
                            },
                          }}
                        >
                          <Send fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </Paper>
              </Box>

              {/* Right Sidebar - Scrollable */}
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: activeForm ? '0 0 calc(58.333% - 8px)' : activeDeployment ? '0 0 calc(41.666% - 8px)' : '0 0 calc(33.333% - 8px)', minHeight: 0, transition: 'all 0.3s ease' }}>
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowX: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    pr: 0.5,
                    '&::-webkit-scrollbar': {
                      width: '4px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: 'rgba(0,0,0,0.12)',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      background: 'rgba(0,0,0,0.2)',
                    },
                  }}
                >
                  {/* Dynamic Form Panel — temporarily replaces widgets */}
                  {activeForm && (
                    <DynamicFormMessage
                      key={activeForm.id}
                      formData={activeForm.formData}
                      onSubmit={(payload) => handleFormSubmit(payload)}
                      fieldUpdates={formFieldUpdates}
                    />
                  )}
                  {!activeForm && activeDeployment && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <IconButton
                          size="small"
                          onClick={() => setActiveDeployment(null)}
                          sx={{ color: COLORS.textMuted, '&:hover': { color: COLORS.textPrimary } }}
                        >
                          <Stop sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                      <DeploymentTracker
                        data={activeDeployment}
                        onTriggerDeploy={(owner, repo, workflow) => {
                          if (!isChatBotTyping) {
                            processMessage(
                              `Deploy repository ${owner}/${repo} using workflow ${workflow}`,
                            );
                          }
                        }}
                        onRefreshStatus={(owner, repo, runId) => {
                          if (!isChatBotTyping) {
                            processMessage(
                              runId
                                ? `Check deployment status for ${owner}/${repo} run ${runId}`
                                : `Check deployment status for ${owner}/${repo}`,
                            );
                          }
                        }}
                      />
                    </Box>
                  )}
                  {!activeForm && !activeDeployment && (
                  <>
                  {/* Stats Cards */}
                  <Box id="tour-stats-cards">
                    <Grid container spacing={1.5}>
                      <Grid item xs={6}>
                        <Link to="/catalog/default/component?filters[type]=project&filters[user]=all" style={{ textDecoration: 'none' }}>
                          <Card elevation={0} sx={{ ...STYLES.cardBase, height: '100%' }}>
                            <CardContent sx={STYLES.statCardContent}>
                              <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.8125rem', fontWeight: '400', color: 'rgba(0,0,0,0.5)' }}>
                                Projects
                              </Typography>
                              <Typography variant="h5" sx={{ my: 0.5, fontSize: '1.5rem !important', fontWeight: '700 !important', color: COLORS.textPrimary }}>
                                {statsLoading ? '...' : statsData.projects.total}
                              </Typography>
                              {(!statsLoading && statsData.projects.new > 0) && (
                                <Typography variant="body2" sx={{ color: COLORS.green, fontSize: '0.75rem', fontWeight: '500' }}>
                                  {`+${statsData.projects.new} new`}
                                </Typography>
                              )}
                            </CardContent>
                          </Card>
                        </Link>
                      </Grid>
                      <Grid item xs={6}>
                        <Link to="/catalog/default/component?filters[type]=model&filters[user]=all" style={{ textDecoration: 'none' }}>
                          <Card elevation={0} sx={{ ...STYLES.cardBase, height: '100%' }}>
                            <CardContent sx={STYLES.statCardContent}>
                              <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.8125rem', fontWeight: '400', color: 'rgba(0,0,0,0.5)' }}>
                                ML Models
                              </Typography>
                              <Typography variant="h5" sx={{ my: 0.5, fontSize: '1.5rem !important', fontWeight: '700 !important', color: COLORS.textPrimary }}>
                                {statsLoading ? '...' : statsData.models.total}
                              </Typography>
                              {(!statsLoading && statsData.models.new > 0) && (
                                <Typography variant="body2" sx={{ color: COLORS.green, fontSize: '0.75rem', fontWeight: '500' }}>
                                  {`+${statsData.models.new} new`}
                                </Typography>
                              )}
                            </CardContent>
                          </Card>
                        </Link>
                      </Grid>
                      <Grid item xs={6}>
                        <Link to="/catalog/default/component?filters[type]=documentation&filters[user]=all" style={{ textDecoration: 'none' }}>
                          <Card elevation={0} sx={{ ...STYLES.cardBase, height: '100%' }}>
                            <CardContent sx={STYLES.statCardContent}>
                              <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.8125rem', fontWeight: '400', color: 'rgba(0,0,0,0.5)' }}>
                                Docs
                              </Typography>
                              <Typography variant="h5" sx={{ my: 0.5, fontSize: '1.5rem !important', fontWeight: '700 !important', color: COLORS.textPrimary }}>
                                {statsLoading ? '...' : statsData.docs.total}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Link>
                      </Grid>
                      <Grid item xs={6}>
                        <Link to="/cost-explorer" style={{ textDecoration: 'none' }}>
                          <Card elevation={0} sx={{ ...STYLES.cardBase, height: '100%' }}>
                            <CardContent sx={STYLES.statCardContent}>
                              <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.8125rem', fontWeight: '400', color: 'rgba(0,0,0,0.5)' }}>
                                Cost
                              </Typography>
                              <Typography variant="h5" sx={{ my: 0.5, fontSize: '1.5rem !important', fontWeight: '700 !important', color: COLORS.textPrimary }}>
                                {statsLoading ? '...' : statsData.cost.value}
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.6875rem', fontWeight: '400', color: 'rgba(0,0,0,0.4)' }}>
                                Last 30 Days
                              </Typography>
                            </CardContent>
                          </Card>
                        </Link>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Recent Projects */}
                  {recentProjects.length > 0 && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        My Recent Projects
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {recentProjects.map((project, idx) => (
                          <Link key={idx} to={`/catalog/default/component/${project.metadata?.name}`}>
                            <Card elevation={0} sx={{ ...STYLES.cardBase, cursor: 'pointer' }}>
                              <CardContent sx={{ p: 1, '&:last-child': { pb: '16px' } }}>
                                <Typography variant="body2"
                                  sx={{
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    color: COLORS.textPrimary,
                                  }}
                                >
                                  {project.metadata?.name}
                                </Typography>
                                {project.spec?.type && (
                                  <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'rgba(0,0,0,0.6)' }}>
                                    {project.spec.type}
                                  </Typography>
                                )}
                              </CardContent>
                            </Card>
                          </Link>
                        ))}
                      </Box>
                    </Box>
                  )}
                  {/* Platform Tools */}
                  <Box id="tour-tools-section">
                    <Typography variant="h6" sx={{ fontWeight: '500 !important', fontSize: '0.875rem !important', color: 'rgba(0,0,0,0.85)', mb: 1 }}>
                      Platform Tools
                    </Typography>
                    <Grid container spacing={1.5}>
                      {tools.map((tool, idx) => (
                        <Grid item xs={6} key={idx}>
                          <Card elevation={0} sx={{ ...STYLES.cardBase, height: '100%' }}>
                            <CardActionArea
                              style={{ height: '100%', borderRadius: '16px' }}
                              onClick={() => window.open(tool.url, '_blank')}
                            >
                              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                  <Typography sx={{ fontSize: 18 }}>
                                    {tool.icon}
                                  </Typography>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: '500', display: 'block', color: 'rgba(0,0,0,0.85)' }}>
                                      {tool.label}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '0.6875rem', color: 'rgba(0,0,0,0.45)', display: 'block', mt: 0.25 }}>
                                      {tool.description}
                                    </Typography>
                                  </Box>
                                  <Launch style={{ fontSize: 12, color: 'rgba(0,0,0,0.3)' }} />
                                </Box>
                              </CardContent>
                            </CardActionArea>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>

                  {/* Starred Entities */}
                  <Box id="tour-starred-entities">
                    <Card
                      elevation={0}
                      sx={{
                        ...STYLES.cardBase,
                        overflow: 'hidden',
                        '& .MuiCardHeader-title': {
                          fontSize: '1rem !important',
                          fontWeight: '500 !important',
                          color: 'rgba(0,0,0,0.85)',
                          paddingLeft: '8px',
                        },
                      }}
                    >
                      <HomePageStarredEntities
                        title="⭐ Favorite Entities"
                        noStarredEntitiesMessage="Click the star icon on any component, API, or resource to add it here. Your starred items will appear as shortcuts for easy navigation."
                      />
                    </Card>
                  </Box>
                  </>
                  )}
                </Box>
              </Box>
            </Box>
          </Container>
        </Box>
      </Box>
    </SearchContextProvider>
  );
};
